import { Box, Button, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined'
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import LinearScaleOutlinedIcon from '@mui/icons-material/LinearScaleOutlined'
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined'
import SortOutlinedIcon from '@mui/icons-material/SortOutlined'
import type { SvgIconComponent } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import QuestionEditor from './QuestionEditor'
import { makeQuestion, type EditableQuestion, type QuestionEditorConfig, type QuestionKind } from './types'

const KIND_ICON: Record<QuestionKind, SvgIconComponent> = {
  text: TitleOutlinedIcon,
  single: FormatListBulletedOutlinedIcon,
  multi: ChecklistOutlinedIcon,
  scale: LinearScaleOutlinedIcon,
  nps: StarBorderOutlinedIcon,
  rank: SortOutlinedIcon,
}

type QuestionListEditorProps = {
  questions: EditableQuestion[]
  onChange: (next: EditableQuestion[]) => void
  config: QuestionEditorConfig
}

/**
 * Reusable question builder — a list of editable questions plus an add-by-type
 * picker. Used by the questionnaire-template editor and the create-study wizard.
 */
export default function QuestionListEditor({ questions, onChange, config }: QuestionListEditorProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('questions')

  const patch = (id: string, p: Partial<EditableQuestion>) =>
    onChange(questions.map((q) => (q.id === id ? { ...q, ...p } : q)))
  const remove = (id: string) => onChange(questions.filter((q) => q.id !== id))
  const add = (kind: QuestionKind) => onChange([...questions, makeQuestion(kind, !!config.showOptionValues)])

  const AddPicker = (
    <Paper
      sx={{
        border: `1.5px dashed ${c.ink32}`,
        borderRadius: `${theme.affexy.radii.md}px`,
        p: 2.25,
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.ink }}>{t('addQuestion')}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {config.allowedKinds.map((kind) => {
          const Icon = KIND_ICON[kind]
          return (
            <Button
              key={kind}
              onClick={() => add(kind)}
              startIcon={<Icon sx={{ fontSize: 15, color: c.coral }} />}
              sx={{
                bgcolor: c.gray50,
                border: `1px solid ${c.ink08}`,
                color: c.ink72,
                fontSize: 13,
                '&:hover': { bgcolor: '#fff', borderColor: c.coral },
              }}
            >
              {t(`kinds.${kind}`)}
            </Button>
          )
        })}
      </Box>
    </Paper>
  )

  return (
    <Stack spacing={1.5}>
      {questions.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: c.ink56 }}>{t('emptyHint')}</Typography>
      ) : (
        questions.map((q, i) => (
          <QuestionEditor
            key={q.id}
            question={q}
            index={i}
            config={config}
            onChange={(p) => patch(q.id, p)}
            onDelete={() => remove(q.id)}
          />
        ))
      )}
      <Box sx={{ mt: 0.5 }}>{AddPicker}</Box>
    </Stack>
  )
}
